import React, { createContext, useState, useContext, ReactNode, Dispatch, SetStateAction } from 'react';

interface OrderViewContextType {
  expandedOrderId: number | null;
  setExpandedOrderId: Dispatch<SetStateAction<number | null>>;
  localAccessories: Record<number, AccessoryItem[]>;
  localServices: Record<number, AdditionalServiceItem[]>;
  localGradings: Record<number, Grading[]>;
  addLocalAccessory: (orderId: number, item: AccessoryItem) => void;
  addLocalService: (orderId: number, item: AdditionalServiceItem) => void;
  addLocalGrading: (orderId: number, item: Grading) => void;
  removeLocalAccessory: (orderId: number, variantId: number) => void;
  removeLocalService: (orderId: number, item: AdditionalServiceItem) => void;
  removeLocalGrading: (orderId: number, gradingId: number) => void;
}

const OrderViewContext = createContext<OrderViewContextType | undefined>(undefined);

export const OrderViewProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [expandedOrderId, setExpandedOrderId] = useState<number | null>(null);
  const [localAccessories, setLocalAccessories] = useState<Record<number, AccessoryItem[]>>({});
  const [localServices, setLocalServices] = useState<Record<number, AdditionalServiceItem[]>>({});
  const [localGradings, setLocalGradings] = useState<Record<number, Grading[]>>({});

  const addLocalAccessory = (orderId: number, item: AccessoryItem) => {
    setLocalAccessories(prev => {
      const existing = prev[orderId] ?? [];
      const idx = existing.findIndex(a => a.variantId === item.variantId);
      if (idx >= 0) {
        const updated = [...existing];
        updated[idx] = { ...updated[idx], quantity: updated[idx].quantity + item.quantity };
        return { ...prev, [orderId]: updated };
      }
      return { ...prev, [orderId]: [...existing, item] };
    });
  };

  const addLocalService = (orderId: number, item: AdditionalServiceItem) => {
    setLocalServices(prev => ({
      ...prev,
      [orderId]: [...(prev[orderId] ?? []), item],
    }));
  };

  const addLocalGrading = (orderId: number, item: Grading) => {
    setLocalGradings(prev => ({
      ...prev,
      [orderId]: [...(prev[orderId] ?? []), item],
    }));
  };

  const removeLocalGrading = (orderId: number, gradingId: number) => {
    setLocalGradings(prev => ({
      ...prev,
      [orderId]: (prev[orderId] ?? []).filter(g => g.id !== gradingId),
    }));
  };

  const removeLocalAccessory = (orderId: number, variantId: number) => {
    setLocalAccessories(prev => ({
      ...prev,
      [orderId]: (prev[orderId] ?? []).filter(a => a.variantId !== variantId),
    }));
  };

  const removeLocalService = (orderId: number, item: AdditionalServiceItem) => {
    setLocalServices(prev => {
      const existing = prev[orderId] ?? [];
      const idx = existing.indexOf(item);
      if (idx === -1) return prev;
      const updated = [...existing];
      updated.splice(idx, 1);
      return { ...prev, [orderId]: updated };
    });
  };

  return (
    <OrderViewContext.Provider value={{
      expandedOrderId,
      setExpandedOrderId,
      localAccessories,
      localServices,
      localGradings,
      addLocalAccessory,
      addLocalService,
      addLocalGrading,
      removeLocalAccessory,
      removeLocalService,
      removeLocalGrading,
    }}>
      {children}
    </OrderViewContext.Provider>
  );
};

export const useOrderView = (): OrderViewContextType => {
  const context = useContext(OrderViewContext);
  if (!context) {
    throw new Error('useOrderView must be used within OrderViewProvider');
  }
  return context;
};
